import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, FormField, Heading, Input } from '@insolech/ui';
import { supabase } from '@/lib/supabase';

/**
 * AuthInvitePage — `/auth/invite/:token`.
 *
 * Steps:
 *   1. Look up the invitation row by its public token (anon-safe lookup via the
 *      `accept-clinic-invitation` Edge Function — but we don't call it yet).
 *   2. Ask the visitor to either sign in (existing user) or pick a password
 *      (new user). On submit, we either signInWithPassword OR
 *      signUp({ email, password, options: { data: { full_name }} }).
 *   3. Once a session exists, POST { token } to the
 *      `accept-clinic-invitation` Edge Function which creates the
 *      `clinic_members` row.
 *   4. Redirect to /dashboard.
 *
 * The Edge Function is the source of truth — we don't write to clinic_members
 * from the client.
 */

const FUNCTIONS_URL = (import.meta.env.VITE_FUNCTIONS_URL ?? '').replace(/\/$/, '');

interface InviteSummary {
  clinic_id: string;
  email: string;
  role: 'owner' | 'member';
  expires_at: string;
  clinic_name: string | null;
}

export function AuthInvitePage() {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [invite, setInvite] = React.useState<InviteSummary | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [mode, setMode] = React.useState<'signin' | 'signup'>('signin');
  const [fullName, setFullName] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setError('Invitation token missing.');
        setLoading(false);
        return;
      }
      // Anon-safe metadata lookup — relies on the future "lookup-only" route.
      // Phase 4a degrades gracefully: even if we cannot pre-fetch, the user
      // can still submit and the Edge Function will return a friendly error.
      const { data, error: qErr } = await supabase
        .from('clinic_invitations')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .select('clinic_id, email, role, expires_at, clinics(name)' as any)
        .eq('token', token)
        .maybeSingle();
      if (cancelled) return;
      if (qErr || !data) {
        // Anon often can't see this row by RLS — treat as "we can't tell yet"
        // rather than showing a hard error.
        setInvite(null);
      } else {
        const row = data as unknown as {
          clinic_id: string;
          email: string;
          role: 'owner' | 'member';
          expires_at: string;
          clinics: { name: string } | null;
        };
        setInvite({
          clinic_id: row.clinic_id,
          email: row.email,
          role: row.role,
          expires_at: row.expires_at,
          clinic_name: row.clinics?.name ?? null,
        });
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const email = invite?.email ?? '';
      if (!email) {
        throw new Error('We could not pre-fill your email. Ask the admin to resend the invite.');
      }
      let session = (await supabase.auth.getSession()).data.session;

      if (!session) {
        if (mode === 'signin') {
          const { error: sErr } = await supabase.auth.signInWithPassword({ email, password });
          if (sErr) throw sErr;
        } else {
          const { error: sErr } = await supabase.auth.signUp({
            email,
            password,
            options: {
              data: { full_name: fullName || email.split('@')[0] },
              emailRedirectTo: `${window.location.origin}/auth/callback`,
            },
          });
          if (sErr) throw sErr;
        }
        session = (await supabase.auth.getSession()).data.session;
      }

      if (!session) {
        // Sign-up may need email confirmation before a session exists; the
        // user will land back here via the email confirmation link. Show a
        // friendly explanation rather than failing.
        setError(
          'Check your email to confirm your account, then click the invitation link again to finish joining.',
        );
        setSubmitting(false);
        return;
      }

      // Hand off to Edge Function to create the clinic_members row
      const accessToken = session.access_token;
      const res = await fetch(`${FUNCTIONS_URL}/accept-clinic-invitation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ token }),
      });
      const json = (await res.json().catch(() => ({}))) as {
        data?: unknown;
        error?: { code?: string; message?: string } | null;
      };
      if (!res.ok || json?.error) {
        throw new Error(json?.error?.message ?? 'Could not accept the invitation.');
      }
      navigate('/dashboard', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-surface-base">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
      </main>
    );
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-surface-base p-6">
      <div className="w-full max-w-md">
        <Heading as="h1" size="h3">
          Join {invite?.clinic_name ?? 'your clinic'} on INSOLECH
        </Heading>
        <p className="mt-2 text-text-secondary text-sm">
          {invite?.email
            ? `Invited as ${invite.email}.`
            : 'Sign in or create your account to accept the invitation.'}
        </p>

        {error ? (
          <Alert tone="error" title="Couldn't accept the invitation" className="mt-6">
            {error}
          </Alert>
        ) : null}

        <div className="mt-6 flex gap-2">
          <button
            type="button"
            className={`text-sm px-3 py-1 max-sm:py-2.5 rounded-md ${mode === 'signin' ? 'bg-brand-50 text-brand-700' : 'text-text-secondary'}`}
            onClick={() => setMode('signin')}
          >
            I have an account
          </button>
          <button
            type="button"
            className={`text-sm px-3 py-1 max-sm:py-2.5 rounded-md ${mode === 'signup' ? 'bg-brand-50 text-brand-700' : 'text-text-secondary'}`}
            onClick={() => setMode('signup')}
          >
            Create my account
          </button>
        </div>

        <form className="mt-4 flex flex-col gap-4" onSubmit={onSubmit}>
          {mode === 'signup' ? (
            <FormField label="Full name" required>
              <Input
                type="text"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </FormField>
          ) : null}
          {invite?.email ? (
            <FormField label="Email">
              <Input type="email" disabled value={invite.email} readOnly />
            </FormField>
          ) : null}
          <FormField label="Password" required>
            <Input
              type="password"
              required
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </FormField>
          <Button type="submit" size="lg" disabled={submitting}>
            {submitting ? 'Joining…' : 'Accept invitation'}
          </Button>
        </form>
      </div>
    </main>
  );
}
